import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Sun, Calendar, X, Save, Loader2, 
  AlertCircle
} from 'lucide-react';
import './AdminHolidays.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    name: ''
  });

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/holidays`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHolidays(data);
      }
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormData({ date: '', name: '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/holidays`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchHolidays();
        setShowModal(false);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add holiday');
      }
    } catch (error) {
      console.error('Failed to save holiday:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteHoliday = async (holidayId) => {
    if (!window.confirm('Are you sure you want to remove this holiday?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/holidays/${holidayId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchHolidays();
      }
    } catch (error) {
      console.error('Failed to delete holiday:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isPastDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const upcomingHolidays = holidays.filter(h => !isPastDate(h.date));
  const pastHolidays = holidays.filter(h => isPastDate(h.date));

  return (
    <div className="admin-holidays" data-testid="admin-holidays">
      <div className="page-header">
        <div>
          <h1>Holidays</h1>
          <p>Block dates when you're not available for bookings</p>
        </div>
        <button className="add-btn" onClick={openCreateModal} data-testid="add-holiday-btn">
          <Plus size={18} />
          <span>Add Holiday</span>
        </button>
      </div>

      <div className="info-banner">
        <AlertCircle size={18} />
        <p>Holidays will automatically block the booking calendar for clients on these dates.</p>
      </div>

      {loading ? (
        <div className="loading-container">
          <Loader2 className="spin" size={32} />
          <span>Loading holidays...</span>
        </div>
      ) : holidays.length === 0 ? (
        <div className="empty-container">
          <Sun size={48} />
          <h3>No holidays added</h3>
          <p>Add dates when you're not available to block the booking calendar.</p>
          <button className="add-btn" onClick={openCreateModal}>
            <Plus size={18} />
            <span>Add First Holiday</span>
          </button>
        </div>
      ) : (
        <div className="holidays-sections">
          {upcomingHolidays.length > 0 && (
            <div className="holiday-section">
              <h2 className="section-title">
                <Calendar size={18} />
                Upcoming Holidays ({upcomingHolidays.length})
              </h2>
              <div className="holidays-list">
                {upcomingHolidays.map((holiday) => (
                  <div 
                    key={holiday.id} 
                    className="holiday-card"
                    data-testid={`holiday-card-${holiday.id}`}
                  >
                    <div className="holiday-icon">
                      <Sun size={20} />
                    </div>
                    <div className="holiday-info">
                      <span className="holiday-name">{holiday.name}</span>
                      <span className="holiday-date">{formatDate(holiday.date)}</span>
                    </div>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteHoliday(holiday.id)}
                      data-testid={`delete-holiday-${holiday.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pastHolidays.length > 0 && (
            <div className="holiday-section past">
              <h2 className="section-title">
                <Calendar size={18} />
                Past Holidays ({pastHolidays.length})
              </h2>
              <div className="holidays-list">
                {pastHolidays.map((holiday) => (
                  <div 
                    key={holiday.id} 
                    className="holiday-card past"
                    data-testid={`holiday-card-${holiday.id}`}
                  >
                    <div className="holiday-icon">
                      <Sun size={20} />
                    </div>
                    <div className="holiday-info">
                      <span className="holiday-name">{holiday.name}</span>
                      <span className="holiday-date">{formatDate(holiday.date)}</span>
                    </div>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteHoliday(holiday.id)}
                      data-testid={`delete-holiday-${holiday.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Holiday Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="holiday-modal">
            <div className="modal-header">
              <h2>Add Holiday</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="holiday-form">
              <div className="form-group">
                <label>Holiday Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Diwali, Company Holiday"
                  required
                  data-testid="holiday-name-input"
                />
              </div>

              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="holiday-date-input"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-save" 
                  disabled={saving}
                  data-testid="save-holiday-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      <span>Add Holiday</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHolidays;
